/**
 * Sidebar parts Keystone adds, each composed from the kit's own or drawn to its measure: the search
 * field as the palette's door, nav rows that are links (so a destination opens in a new tab), a
 * count on a row, a tree whose row is a link too, and the account card as a menu.
 * See docs/design/kit-requests.md; when Sidebar's parts take `href`, a trailing slot and a menu, this file shrinks.
 */
import type { KeyboardEvent, ReactNode } from "react";
import { Count } from "./Badge";
import { cx, focusRing } from "./cx";
import { Graphic, Icon, type IconName } from "./icons";
import { Menu, MenuContent, MenuGroup, MenuTrigger } from "./Menu";
import { SearchInput, SearchInputHint } from "./SearchInput";

/** The sidebar's search field as the palette's door: a click, Enter, or any typing opens it. ⌘K is the shell's. */
export function SidebarCommand({ label, onOpen }: { label: string; onOpen: () => void }) {
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
      event.preventDefault();
      onOpen();
    }
  };
  return (
    <SearchInput
      label={label}
      raised
      readOnly
      aria-haspopup="dialog"
      aria-keyshortcuts="Meta+K Control+K"
      onClick={onOpen}
      onKeyDown={onKeyDown}
      // The field fills its frame, so the whole 36 px row is the target.
      className="w-full shrink-0 cursor-pointer [&_input]:h-9 [&_input]:cursor-pointer"
      trailing={
        <SearchInputHint>
          <span className="flex size-4 items-center justify-center rounded p-0.5">
            <Icon name="command" size={12} />
          </span>
          <span className="flex size-4 items-center justify-center rounded p-0.5 text-caption font-medium leading-none text-kbd-foreground">K</span>
        </SearchInputHint>
      }
    />
  );
}

interface SidebarLinkProps {
  label: string;
  icon: IconName;
  href: string;
  /** The current page: the white pill. */
  active?: boolean;
  /** Fainter ink, for secondary groups. */
  muted?: boolean;
  /** A 40 px row with a tighter icon gap. */
  tall?: boolean;
  /** How many wait behind the link; nothing is drawn for 0 or undefined. */
  count?: number;
  /** What is counted, for assistive tech ("open"). */
  unit?: string;
}

/** SidebarItem as a link, to SidebarItem's measure, with an optional count at the right edge. */
export function SidebarLink({ label, icon, href, active = false, muted = false, tall = false, count, unit }: SidebarLinkProps) {
  return (
    <a
      href={href}
      aria-current={active ? "page" : undefined}
      className={cx(
        "group/nav flex w-full items-center justify-between gap-2 rounded-lg border-hair px-2.5 py-[7px] text-left text-body leading-5 transition-[background-color,box-shadow,border-color,color] duration-150",
        focusRing,
        tall ? "min-h-10" : "min-h-9",
        active ? "border-border bg-card font-medium text-brand shadow-nav" : "border-transparent hover:bg-card/70 hover:text-foreground",
        !active && (muted ? "text-muted-foreground" : "text-secondary-foreground"),
      )}
    >
      <span className={cx("flex min-w-0 items-center", tall ? "gap-2" : "gap-2.5")}>
        {/* The active row's icon stands on a 24 px wash of the brand tint; the box keeps the icon where it was. */}
        <span className={cx("-m-[3px] flex size-6 shrink-0 items-center justify-center rounded-md transition-[transform,background-color] duration-150 ease-out group-hover/nav:scale-110", active && "bg-brand-tint")}>
          <Icon name={icon} size={18} brand={active} />
        </span>
        {/* The label wraps before the count gives way (larger text, wider spacing). */}
        <span className="min-w-0">{label}</span>
      </span>
      {/* One ink on every row, the lit one included. */}
      {!!count && <Count value={count} tone="quiet" label={unit ? `${count} ${unit}` : undefined} />}
    </a>
  );
}

interface SidebarTreeProps extends Pick<SidebarLinkProps, "label" | "icon" | "href" | "active"> {
  open: boolean;
  onToggle: () => void;
  /** Names the toggle for assistive tech ("People views"). */
  toggleLabel: string;
  /** SidebarSubLinks. */
  children: ReactNode;
}

/** SidebarItem's tree with a row that is a link: the label goes to the place, the chevron opens its views. */
export function SidebarTree({ label, icon, href, active, open, onToggle, toggleLabel, children }: SidebarTreeProps) {
  return (
    <div className="flex w-full flex-col">
      <div className="relative w-full">
        <SidebarLink label={label} icon={icon} href={href} active={active} tall />
        <button type="button" onClick={onToggle} aria-expanded={open} aria-label={toggleLabel} className={cx("absolute right-1 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-md transition-colors hover:bg-accent", focusRing)}>
          <span className={cx("flex transition-transform duration-200 ease-out-smooth", !open && "rotate-180")}>
            <Icon name="chevron-up" size={12} />
          </span>
        </button>
      </div>
      {/* Closed, its links are out of reach of the keyboard and of assistive tech. */}
      <div inert={!open || undefined} className={cx("grid transition-[grid-template-rows,opacity,margin] duration-200 ease-in-out", open ? "mt-0.5 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0")}>
        <div className="overflow-hidden">
          <div className="flex flex-col gap-0.5">{children}</div>
        </div>
      </div>
    </div>
  );
}

/** SidebarSubItem as a link: a leaf of a tree, hung off the connector line. */
export function SidebarSubLink({ label, href, active = false }: { label: string; href: string; active?: boolean }) {
  return (
    <a href={href} aria-current={active ? "page" : undefined} className="group/sub relative flex h-8 w-full items-center justify-end pl-2.5 outline-none">
      <span
        className={cx(
          "flex h-full w-[calc(100%-28px)] items-center rounded-md text-meta leading-none transition-[color,background-color,padding] duration-200 group-hover/sub:bg-card/70 group-hover/sub:pl-1.5 group-hover/sub:text-foreground group-focus-visible/sub:ring-[3px] group-focus-visible/sub:ring-ring",
          active ? "pl-1.5 font-medium text-brand" : "px-0 text-muted-foreground",
        )}
      >
        {label}
      </span>
      <span className="pointer-events-none absolute left-[18px] top-[-3px] h-5 w-2">
        <span className="pointer-events-none absolute block" style={{ inset: "-2% -5%" }}>
          <Graphic name="tree-line" className="block size-full max-w-none" />
        </span>
      </span>
      <span className="pointer-events-none absolute left-[23px] top-[15px] size-1">
        <Graphic name="tree-dot" className={cx("absolute inset-0 block size-full transition-transform duration-200", active && "scale-150")} />
      </span>
    </a>
  );
}

interface SidebarAccountProps {
  name: string;
  detail: string;
  initials: string;
  /** Names the menu for assistive tech ("Account"). */
  label: string;
  /** MenuItems. */
  children: ReactNode;
}

/** SidebarUser as the menu its selector arrows promise: the account card opens a menu above itself. */
export function SidebarAccount({ name, detail, initials, label, children }: SidebarAccountProps) {
  return (
    <Menu>
      <MenuTrigger>
        <button
          type="button"
          aria-label={`${label}: ${name}`}
          className={cx(
            "group/acct flex w-full shrink-0 items-center gap-2 rounded-xl border-hair border-border bg-card py-2.5 pl-2.5 pr-3 text-left drop-shadow-card transition-[box-shadow,transform] duration-200 hover:shadow-lift active:scale-[0.99] data-[state=open]:shadow-lift",
            focusRing,
          )}
        >
          <span className="relative flex size-9 shrink-0 items-center justify-center rounded-full border-hair border-avatar-border bg-secondary p-2">
            <span className="whitespace-nowrap text-caption font-medium leading-none text-secondary-foreground">{initials}</span>
          </span>
          <span className="flex min-w-0 flex-1 flex-col justify-center gap-2 whitespace-nowrap leading-none">
            <span data-name className="truncate text-body font-medium text-foreground">
              {name}
            </span>
            <span data-name className="text-meta break-all whitespace-normal text-muted-foreground">
              {detail}
            </span>
          </span>
          <span className="flex transition-transform duration-200 group-hover/acct:scale-110">
            <Icon name="selector" size={18} />
          </span>
        </button>
      </MenuTrigger>
      <MenuContent side="top" align="start" className="w-(--radix-dropdown-menu-trigger-width)">
        <MenuGroup>{children}</MenuGroup>
      </MenuContent>
    </Menu>
  );
}
